import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  DocumentIcon, 
  CurrencyDollarIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

const CreditConfirmModal = ({
  isOpen,
  onClose,
  fileName,
  userCredits,
  onConfirm
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearError, setYearError] = useState('');
  const [selectedPassword, setSelectedPassword] = useState('');
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);

  // Simple credit check - require at least 1 credit
  const hasSufficientCredits = userCredits >= 1;

  // Handle modal visibility with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => setIsVisible(false), 150);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Validate year input
  const validateYear = (year) => {
    // Allow empty year (no validation needed)
    if (!year || year === '') {
      setYearError('');
      return true;
    }
    
    // Validate only when year is provided
    const numYear = parseInt(year);
    if (isNaN(numYear) || numYear < 1990 || numYear > 2030) {
      setYearError('Year must be between 1990 and 2030');
      return false;
    }
    setYearError('');
    return true;
  };

  // Handle year input change
  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    if (year) {
      validateYear(year);
    }
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    setSelectedPassword(e.target.value);
  };

  // Handle password protection checkbox
  const handlePasswordProtectedChange = (e) => {
    setIsPasswordProtected(e.target.checked);
    if (!e.target.checked) {
      setSelectedPassword(''); // Clear password if unchecked
    }
  };

  // Handle confirm action
  const handleConfirm = async () => {
    if (!hasSufficientCredits || isConfirming) return;

    if (!validateYear(selectedYear)) return;

    // Validate password if protection is enabled
    if (isPasswordProtected && !selectedPassword.trim()) {
      alert('Please enter the PDF password or uncheck the password protection option.');
      return;
    }

    console.log('🔍 SUBMITTING DATA:', {
      selectedYear,
      parsedYear: selectedYear ? parseInt(selectedYear) : null,
      hasPassword: isPasswordProtected,
      passwordLength: selectedPassword.length
    });

    setIsConfirming(true);
    try {
      await onConfirm({
        year: selectedYear ? parseInt(selectedYear) : null,
        password: isPasswordProtected ? selectedPassword : null
      });
    } catch (error) {
      console.error('Conversion error:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen 
          ? 'opacity-100 backdrop-blur-sm bg-black/50' 
          : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`relative w-full max-w-md bg-white rounded-xl shadow-2xl transform transition-all duration-300 ${
          isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
          <h2 
            id="modal-title" 
            className="text-xl font-semibold text-gray-900"
          >
            Convert Bank Statement
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* File Information */}
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <DocumentIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
            <p className="text-sm font-medium text-gray-900 truncate">
              {fileName || 'bank_statement.pdf'}
            </p>
          </div>

          {/* Year Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-900">
              Statement Year
            </label>
            <input
              type="number"
              value={selectedYear}
              onChange={handleYearChange}
              min="1990"
              max="2030"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                yearError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter year (e.g., 2025)"
            />
            {yearError && (
              <p className="text-xs text-red-600">{yearError}</p>
            )}
            <p className="text-xs text-gray-500">
              Used for dates that don't include years in the original statement
            </p>
          </div>

          {/* Password Protection Section */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="passwordProtected"
                checked={isPasswordProtected}
                onChange={handlePasswordProtectedChange}
                className="h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="passwordProtected" className="text-sm font-medium text-gray-900">
                This PDF is password protected
              </label>
            </div>

            {isPasswordProtected && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  PDF Password
                </label>
                <input
                  type="password"
                  value={selectedPassword}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter PDF password"
                  autoComplete="off"
                />
              </div>
            )}
          </div>

          {/* Processing Information */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <CurrencyDollarIcon className="w-5 h-5 text-amber-600" />
              <h3 className="text-sm font-medium text-gray-900">Processing Cost</h3>
            </div>
            <div className="pl-7">
              <p className="text-xs text-gray-500">
                1 Credit per page (minimum 1 credit)
              </p>
            </div>
          </div>

          {/* Credit Balance */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <CreditCardIcon className="w-5 h-5 text-emerald-600" />
              <h3 className="text-sm font-medium text-gray-900">Your Credits</h3>
            </div>
            <div className="pl-7">
              <p className="text-sm text-gray-600">
                Current balance: 
                <span className="font-semibold text-gray-900 ml-1">
                  {userCredits} {userCredits === 1 ? 'credit' : 'credits'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 p-5 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
            disabled={isConfirming}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasSufficientCredits || isConfirming}
            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all ${
              hasSufficientCredits && !isConfirming
                ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-sm'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            aria-label="Confirm conversion"
          >
            {isConfirming ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Converting...</span>
              </div>
            ) : (
              'Confirm Conversion'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreditConfirmModal;